import client from "../client/client"
import { getCurrentConfig } from "../../config/currentBotConfig"

function requireAgentId(config) {
  if (!config?.agentId) {
    throw new Error('agentId is required in bot config')
  }
  return config.agentId
}

function processSseBuffer(buffer, onEvent) {
  const lines = buffer.split('\n')
  const remainder = lines.pop() ?? ''

  for (const line of lines) {
    if (!line.startsWith('data: ')) continue
    const json = line.slice(6).trim()
    if (!json) continue
    try {
      onEvent(JSON.parse(json))
    } catch (e) {
      console.warn('Failed to parse SSE event:', e.message)
    }
  }

  return remainder
}

const getAllSessions = async (agentId) => {
  const config = getCurrentConfig()
  if (!config) throw new Error('No bot config')
  const resolvedAgentId = agentId || requireAgentId(config)
  const res = await client.get(
    `${config.botApi}/api/sessions/user/me?agentId=${encodeURIComponent(resolvedAgentId)}`
  )
  return await res.json()
}

const getSession = async (sessionId) => {
  const config = getCurrentConfig()
  if (!config) throw new Error('No bot config')
  const res = await client.get(`${config.botApi}/api/sessions/${sessionId}/interactions`)
  return await res.json()
}

const sendMessageStream = async (message, sessionId, { onEvent } = {}) => {
  const config = getCurrentConfig()
  if (!config) throw new Error('No bot config')

  const res = await client.postStream(
    `${config.botApi}/api/runtime/execute/${sessionId}/stream`,
    message
  )

  const reader = res.body?.getReader()
  if (!reader) {
    throw new Error('Streaming is not supported in this browser')
  }

  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    buffer = processSseBuffer(buffer, onEvent)
  }

  processSseBuffer(`${buffer}\n`, onEvent)
}

const sendMessage = async (message, sessionId) => {
  const config = getCurrentConfig()
  if (!config) throw new Error('No bot config')
  const res = await client.post(
    `${config.botApi}/api/runtime/execute/${sessionId}`,
    {},
    message
  )
  return await res.json()
}

const newSession = async (agentIdOverride) => {
  const config = getCurrentConfig()
  if (!config) throw new Error('No bot config')
  const agentId = agentIdOverride || requireAgentId(config)
  const res = await client.post(
    `${config.botApi}/api/sessions`,
    {},
    { agentId }
  )
  return await res.json()
}

const updateSessionName = async (sessionId, transcript) => {
  const config = getCurrentConfig()
  if (!config) throw new Error('No bot config')
  const res = await client.post(
    `${config.botApi}/api/sessions/${sessionId}/name`,
    {},
    { transcript }
  )
  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(errorText || `Failed to update session name (${res.status})`)
  }
  return await res.json()
}

const botApi = {
  newSession,
  sendMessage,
  sendMessageStream,
  updateSessionName,
  getAllSessions,
  getSession,
}

export default botApi
